import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LogOut,
  LayoutDashboard,
  Users,
  Receipt,
  PlusCircle,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";

const adminNav = [
  { to: "/admin", icon: LayoutDashboard, label: "Overview" },
  { to: "/admin/agents", icon: Users, label: "Agents" },
  { to: "/admin/sales", icon: Receipt, label: "All Sales" },
];

const agentNav = [
  { to: "/agent", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/agent/new", icon: PlusCircle, label: "Sell new card" },
  { to: "/agent/sales", icon: Receipt, label: "My sales" },
];

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const nav = isAdmin ? adminNav : agentNav;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Topbar */}
      <header className="sticky top-0 z-40 bg-background/85 backdrop-blur-xl border-b border-border">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={process.env.PUBLIC_URL + "/logo.png"} alt="1Rupee Clinic" className="h-11 w-auto object-contain" />
            <div className="leading-none border-l border-border/70 pl-2.5">
              <div className="font-display font-bold text-sm tracking-tight text-foreground">
                1Rupee <span className="text-primary">Clinic</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5">
                {isAdmin ? "Admin Console" : "Agent Portal"}
              </div>
            </div>
          </Link>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2.5 text-right">
              <div>
                <div className="text-sm font-semibold text-foreground leading-tight">{user?.name}</div>
                <div className="text-[11px] text-muted-foreground">@{user?.username}</div>
              </div>
              <span
                className={cn(
                  "inline-flex h-9 w-9 items-center justify-center rounded-full text-[13px] font-semibold ring-2 ring-primary/15",
                  isAdmin ? "bg-primary text-primary-foreground" : "bg-accent text-accent-foreground"
                )}
              >
                {(user?.name || "?").slice(0, 1).toUpperCase()}
              </span>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-6 lg:py-8 grid lg:grid-cols-[240px_1fr] gap-6 lg:gap-8">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-[88px] lg:h-[calc(100vh-104px)]">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible -mx-1 px-1">
            {nav.map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/admin" || to === "/agent"}
                className={({ isActive }) =>
                  cn(
                    "group flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors whitespace-nowrap",
                    isActive
                      ? "bg-gradient-primary text-primary-foreground shadow-elegant"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  )
                }
              >
                <span className="flex items-center gap-2.5">
                  <Icon className="h-4 w-4" /> {label}
                </span>
                <ChevronRight className="h-4 w-4 opacity-0 group-data-[active=true]:opacity-100 hidden lg:block" />
              </NavLink>
            ))}
          </nav>

          <div className="mt-6 hidden lg:block rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/8 to-accent/8 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
              {isAdmin ? "Live commission" : "Earn per sale"}
            </div>
            <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
              {isAdmin
                ? "Track total revenue, agent payouts and pending commissions in real time."
                : `You earn ${user?.commission_rate ?? 0}% on every ₹100 card you sell. Cards land on the patient’s WhatsApp instantly.`}
            </p>
          </div>
        </aside>

        <main className="min-w-0">{children}</main>
      </div>
    </div>
  );
};

export default DashboardLayout;
